import { useCallback, useEffect, useState } from 'react';
import useSettings from '../hooks/useSettings';

const ALL_COMPONENTS = [
  { type: 'heading', label: 'Überschrift (Heading)' },
  { type: 'text', label: 'Fließtext (Rich Text)' },
  { type: 'image', label: 'Bild & Medien (Image)' },
  { type: 'quote', label: 'Zitat (Quote)' },
  { type: 'container', label: 'Container Block (Nested)' },
  { type: 'divider', label: 'Trennlinie (Divider)' },
  { type: 'spacer', label: 'Abstandshalter (Spacer)' },
  { type: 'html', label: 'Quellcode (HTML)' },
];

const ROLES = [
  { id: 'cms-admin', label: 'Administrator (cms-admin)' },
  { id: 'cms-editor', label: 'Redakteur (cms-editor)' },
  { id: 'cms-viewer', label: 'Betrachter (cms-viewer)' },
];

const DEFAULT_POLICIES = [
  {
    policyId: 'policy-page-standard',
    name: 'Standard Seiten-Richtlinie',
    description: 'Erlaubt alle Standardkomponenten und schachtelbare Container für normale Inhaltsseiten.',
    targetType: 'page',
    allowedComponents: ['heading', 'text', 'image', 'quote', 'container', 'divider', 'spacer'],
    permissions: {
      canCreate: ['cms-admin', 'cms-editor'],
      canEdit: ['cms-admin', 'cms-editor'],
      canDelete: ['cms-admin'],
      canPublish: ['cms-admin'],
    },
    inheritPolicy: true,
    maxDepth: 3,
  },
  {
    policyId: 'policy-container-strict',
    name: 'Strikte Container-Richtlinie',
    description: 'Beschränkt verschachtelte Container auf einfache Texte und Bilder.',
    targetType: 'container',
    allowedComponents: ['heading', 'text', 'image'],
    permissions: {
      canCreate: ['cms-admin', 'cms-editor'],
      canEdit: ['cms-admin', 'cms-editor'],
      canDelete: ['cms-admin'],
      canPublish: ['cms-admin'],
    },
    inheritPolicy: true,
    maxDepth: 2,
  },
];

export default function ContentPolicies() {
  const settingsApi = useSettings();
  const [policies, setPolicies] = useState(DEFAULT_POLICIES);
  const [selectedId, setSelectedId] = useState('policy-page-standard');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadPolicies = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await settingsApi.getSettings();
      if (Array.isArray(data.contentPolicies) && data.contentPolicies.length > 0) {
        setPolicies(data.contentPolicies);
        setSelectedId(data.contentPolicies[0].policyId);
      }
    } catch (err) {
      setError(err.message || 'Fehler beim Laden der Content Policies.');
    } finally {
      setLoading(false);
    }
  }, [settingsApi]);

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  const activePolicy = policies.find((p) => p.policyId === selectedId) || policies[0];

  const updateActivePolicy = (field, value) => {
    setPolicies((prev) =>
      prev.map((p) => (p.policyId === activePolicy.policyId ? { ...p, [field]: value } : p)),
    );
  };

  const toggleAllowedComponent = (compType) => {
    const current = activePolicy.allowedComponents || [];
    const updated = current.includes(compType)
      ? current.filter((c) => c !== compType)
      : [...current, compType];
    updateActivePolicy('allowedComponents', updated);
  };

  const toggleRolePermission = (action, roleId) => {
    const currentPerms = activePolicy.permissions || { canCreate: [], canEdit: [], canDelete: [], canPublish: [] };
    const currentRoles = currentPerms[action] || [];
    const updatedRoles = currentRoles.includes(roleId)
      ? currentRoles.filter((r) => r !== roleId)
      : [...currentRoles, roleId];

    updateActivePolicy('permissions', {
      ...currentPerms,
      [action]: updatedRoles,
    });
  };

  const addPolicy = () => {
    const newId = `policy-${Date.now()}`;
    const newPolicy = {
      policyId: newId,
      name: 'Neue Richtlinie',
      description: 'Benutzerdefinierte Inhalts-Richtlinie',
      targetType: 'page',
      allowedComponents: ['heading', 'text', 'image'],
      permissions: {
        canCreate: ['cms-admin', 'cms-editor'],
        canEdit: ['cms-admin', 'cms-editor'],
        canDelete: ['cms-admin'],
        canPublish: ['cms-admin'],
      },
      inheritPolicy: true,
      maxDepth: 3,
    };
    setPolicies((prev) => [...prev, newPolicy]);
    setSelectedId(newId);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const currentSettings = await settingsApi.getSettings();
      const updatedSettings = await settingsApi.updateSettings({
        ...currentSettings,
        contentPolicies: policies,
      });

      await new Promise((r) => setTimeout(r, 1000));
      const freshSettings = await settingsApi.getSettings(true);
      if (freshSettings && Array.isArray(freshSettings.contentPolicies)) {
        setPolicies(freshSettings.contentPolicies);
      }
      setMessage('Content Policies erfolgreich gespeichert. Die Regeln gelten ab sofort im Editor.');
    } catch (err) {
      setError(err.message || 'Fehler beim Speichern der Content Policies.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="stack gap-lg">
      <section className="card">
        <div className="row between responsive-gap">
          <div>
            <h2>AEM Content Policy Engine</h2>
            <p className="muted">
              Definiere erlaubte Komponententypen, Schachtelungsregeln und Rechte-Matrizen für Seiten & Container-Blöcke.
            </p>
          </div>
          <button className="button primary" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Speichert… (1s Sperre)' : 'Richtlinien speichern'}
          </button>
        </div>
        {message ? <div className="alert success" style={{ marginTop: '1rem' }}>{message}</div> : null}
        {error ? <div className="alert error" style={{ marginTop: '1rem' }}>{error}</div> : null}
      </section>

      {loading ? (
        <p className="muted">Lade Content Policies…</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '1.25rem', minHeight: '600px' }}>
          {/* Left: Policy Selector */}
          <div className="card stack gap-md" style={{ maxHeight: '720px', overflowY: 'auto', opacity: saving ? 0.7 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
            <div className="row between align-center">
              <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>
                Richtlinien ({policies.length})
              </div>
              <button type="button" className="button secondary small" onClick={addPolicy} disabled={saving}>
                + Neu
              </button>
            </div>

            {policies.map((p) => {
              const isSelected = p.policyId === activePolicy.policyId;
              return (
                <div
                  key={p.policyId}
                  onClick={() => setSelectedId(p.policyId)}
                  style={{
                    padding: '0.85rem 1rem',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    border: isSelected ? '2px solid #2563eb' : '1px solid #e2e8f0',
                    background: isSelected ? 'rgba(37,99,235,0.05)' : '#ffffff',
                  }}
                >
                  <div style={{ fontWeight: 700, color: '#0f172a' }}>{p.name}</div>
                  <div className="muted small" style={{ marginTop: '0.2rem' }}>
                    Ziel: <code>{p.targetType}</code> · Allowed: {p.allowedComponents?.length || 0} Bausteine
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right: Policy Editor */}
          <div className="card stack gap-md" style={{ maxHeight: '720px', overflowY: 'auto' }}>
            <div className="row between align-center" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '0.75rem' }}>
              <div>
                <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>
                  Policy ID: {activePolicy.policyId}
                </div>
                <h3 style={{ margin: '0.2rem 0 0' }}>{activePolicy.name}</h3>
              </div>
              <span className="badge" style={{ background: '#dbeafe', color: '#1d4ed8' }}>
                {activePolicy.inheritPolicy ? 'Vererbende Richtlinie (Inherited Scope)' : 'Lokale Richtlinie'}
              </span>
            </div>

            {/* Policy Info Inputs */}
            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <label>
                Richtlinien-Name
                <input
                  type="text"
                  value={activePolicy.name}
                  onChange={(e) => updateActivePolicy('name', e.target.value)}
                />
              </label>
              <label>
                Ziel-Typ (Target Scope)
                <select
                  value={activePolicy.targetType}
                  onChange={(e) => updateActivePolicy('targetType', e.target.value)}
                >
                  <option value="page">Seite (Page Frame)</option>
                  <option value="post">Blog-Beitrag</option>
                  <option value="container">Layout Container</option>
                  <option value="block">Einzelkomponente</option>
                </select>
              </label>
            </div>

            <label>
              Beschreibung
              <input
                type="text"
                value={activePolicy.description || ''}
                onChange={(e) => updateActivePolicy('description', e.target.value)}
                placeholder="Kurze Beschreibung der Richtlinie…"
              />
            </label>

            {/* Whitelist Matrix: Allowed Components */}
            <div>
              <h4 style={{ marginBottom: '0.5rem' }}>1. Erlaubte Komponententypen (Allowed Components Whitelist)</h4>
              <p className="muted small" style={{ marginBottom: '0.75rem' }}>
                Wähle aus, welche Bausteine in diesem Rahmen oder Container eingefügt werden dürfen.
              </p>
              <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.5rem' }}>
                {ALL_COMPONENTS.map((c) => {
                  const checked = (activePolicy.allowedComponents || []).includes(c.type);
                  return (
                    <label
                      key={c.type}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0',
                        background: checked ? '#f0fdf4' : '#ffffff',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAllowedComponent(c.type)}
                      />
                      <span>{c.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Role Action Permission Matrix */}
            <div>
              <h4 style={{ marginBottom: '0.5rem' }}>2. Rechte-Matrix nach Benutzergruppen (Role Capability Matrix)</h4>
              <p className="muted small" style={{ marginBottom: '0.75rem' }}>
                Bestimme, welche Benutzergruppen Aktionen an Knoten mit dieser Richtlinie ausführen dürfen.
              </p>
              <div className="table-wrap">
                <table className="table compact" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th>Benutzerrolle</th>
                      <th style={{ textAlign: 'center' }}>Erstellen (canCreate)</th>
                      <th style={{ textAlign: 'center' }}>Bearbeiten (canEdit)</th>
                      <th style={{ textAlign: 'center' }}>Löschen (canDelete)</th>
                      <th style={{ textAlign: 'center' }}>Publizieren (canPublish)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ROLES.map((role) => {
                      const perms = activePolicy.permissions || {};
                      return (
                        <tr key={role.id}>
                          <td>
                            <strong>{role.label}</strong>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={(perms.canCreate || []).includes(role.id)}
                              onChange={() => toggleRolePermission('canCreate', role.id)}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={(perms.canEdit || []).includes(role.id)}
                              onChange={() => toggleRolePermission('canEdit', role.id)}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={(perms.canDelete || []).includes(role.id)}
                              onChange={() => toggleRolePermission('canDelete', role.id)}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={(perms.canPublish || []).includes(role.id)}
                              onChange={() => toggleRolePermission('canPublish', role.id)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Inheritance & Max Depth Controls */}
            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem', paddingTop: '0.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={Boolean(activePolicy.inheritPolicy)}
                  onChange={(e) => updateActivePolicy('inheritPolicy', e.target.checked)}
                />
                <div>
                  <strong>Richtlinie an Kind-Komponenten vererben</strong>
                  <div className="muted small">Reicht die Whitelist & Rechte an verschachtelte Blöcke weiter.</div>
                </div>
              </label>

              <label>
                Maximale Schachtelungstiefe (Max Depth)
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={activePolicy.maxDepth || 3}
                  onChange={(e) => updateActivePolicy('maxDepth', Number(e.target.value))}
                />
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
